// src/components/TopNav.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { FiBell, FiSettings, FiHelpCircle, FiLogOut, FiChevronDown, FiMenu, FiTrendingUp } from 'react-icons/fi';
import { collection, query, where, onSnapshot, Timestamp, limit } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useAuthUser } from '../../hooks/useAuthUser';
import NotificationDropdown from '../notifications/NotificationDropdown';



function TopNav({ userEmail, onLogout }) {
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const location = useLocation();
  const { userId } = useAuthUser();
  const projectDropdownRef = useRef(null);
  const projectButtonRef = useRef(null);
  const userDropdownRef = useRef(null);
  const userButtonRef = useRef(null);
  const notificationRef = useRef(null);
  const notificationButtonRef = useRef(null);

  // Close dropdowns when route changes or click outside
  useEffect(() => {
    setProjectDropdownOpen(false);
    setUserDropdownOpen(false);
    setNotificationsOpen(false);
    setMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if ( projectDropdownRef.current && !projectDropdownRef.current.contains(event.target) && projectButtonRef.current && !projectButtonRef.current.contains(event.target) ) {
        setProjectDropdownOpen(false);
      }
      if ( userDropdownRef.current && !userDropdownRef.current.contains(event.target) && userButtonRef.current && !userButtonRef.current.contains(event.target) ) {
        setUserDropdownOpen(false);
      }
      if ( notificationRef.current && !notificationRef.current.contains(event.target) && notificationButtonRef.current && !notificationButtonRef.current.contains(event.target) ) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => { document.removeEventListener('mousedown', handleClickOutside); };
  }, []);

  // Fetch unread notification count
  useEffect(() => {
    if (!userId) return;

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const notificationsRef = collection(db, 'users', userId, 'notifications');
    const q = query(
      notificationsRef,
      where('read', '==', false),
      where('createdAt', '>=', Timestamp.fromDate(thirtyDaysAgo)),
      limit(99)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setUnreadCount(snapshot.size);
    }, (err) => {
      setUnreadCount(0);
    });

    return () => unsubscribe();
  }, [userId]);

  // Styling classes
  // Organic Tech: Clean, readable, soft hover states
  const navLinkClass = ({ isActive }) => 
    `px-5 py-2.5 rounded-full text-sm font-bold tracking-wide transition-all duration-300 flex items-center 
     ${ isActive 
        ? 'text-warm-black bg-cream shadow-glow' 
        : 'text-cream/70 hover:text-cream hover:bg-cream/10' 
     }`;

  const dropdownContainerClass = "origin-top-left absolute left-0 mt-3 w-64 rounded-3xl shadow-modal py-3 bg-warm-gray border border-warm-border ring-1 ring-black ring-opacity-5 focus:outline-none z-50 animate-fade-in-up backdrop-blur-xl";
  const dropdownLinkClass = ({ isActive }) => 
    `block px-6 py-3 text-sm font-medium transition-colors duration-200 border-l-2
     ${ isActive 
        ? 'border-slate-blue bg-cream/5 text-slate-blue' 
        : 'border-transparent text-cream/80 hover:bg-cream/5 hover:text-cream' 
     }`;

  const isProjectRouteActive = () => location.pathname.startsWith('/projects') || location.pathname === '/create-project' || location.pathname.startsWith('/project/');
  const isContentHubRouteActive = () => location.pathname === '/content-hub' || location.pathname.includes('/content-hub');


  return (
    <nav className="fixed top-0 w-full z-50 transition-all duration-300 border-b border-warm-border bg-warm-dark/80 backdrop-blur-lg">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          
          {/* Left: Logo & Main Nav */}
          <div className="flex items-center gap-12">
            
            {/* Logo */}
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-3 flex-shrink-0 group">
               <img 
                 src="/images/nextainsight_logo_app.png" 
                 alt="NextAInsight Logo" 
                 className="h-10 w-auto object-contain transition-transform duration-500 group-hover:scale-105"
               />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-baseline space-x-2">
              <NavLink to="/dashboard" className={navLinkClass}>Dashboard</NavLink>
              
              {/* Projects Dropdown */}
              <div className="relative" ref={projectDropdownRef}>
                <button
                  ref={projectButtonRef}
                  onClick={() => setProjectDropdownOpen(!projectDropdownOpen)}
                  aria-expanded={projectDropdownOpen}
                  aria-haspopup="true"
                  className={`flex items-center gap-1 ${navLinkClass({ isActive: isProjectRouteActive() })}`}
                >
                  Projects <FiChevronDown className={`ml-1 w-4 h-4 transition-transform duration-300 ${projectDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {projectDropdownOpen && (
                  <div className={dropdownContainerClass}>
                    <NavLink to="/projects" className={dropdownLinkClass}>All Projects</NavLink>
                    <NavLink to="/create-project" className={dropdownLinkClass}>New Project</NavLink>
                    <div className="border-t border-cream/10 my-1"></div>
                    <span className="px-6 py-2 text-xs font-bold uppercase tracking-widest text-cream/40 block">Recent</span>
                    {/* Placeholder for recent projects could go here */}
                  </div>
                )}
              </div>

               <NavLink to="/content-hub" className={({isActive}) => navLinkClass({isActive: isContentHubRouteActive()})}>Content Hub</NavLink>
               <NavLink to="/trends" className={navLinkClass}><FiTrendingUp className="mr-1.5 h-3.5 w-3.5" />Trends</NavLink>
               <NavLink to="/calendar" className={navLinkClass}>Calendar</NavLink>
               <NavLink to="/analytics" className={navLinkClass}>Analytics</NavLink>
            </div>
          </div>

          {/* Right: Actions & Profile */}
          <div className="hidden md:flex items-center gap-6">
            
             {/* Notifications Bell + Dropdown */}
             <div className="relative" ref={notificationRef}>
                <button
                   ref={notificationButtonRef}
                   onClick={() => setNotificationsOpen(!notificationsOpen)}
                   className="relative p-2 rounded-full hover:bg-cream/10 transition-all text-cream/70 hover:text-slate-blue"
                   aria-expanded={notificationsOpen}
                   aria-haspopup="true"
                >
                   <FiBell className="h-6 w-6" />
                   {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-soft-coral text-white text-[10px] font-bold leading-none px-1 shadow-sm">{unreadCount > 99 ? '99+' : unreadCount}</span>
                   )}
                </button>
                {notificationsOpen && (
                   <NotificationDropdown userId={userId} onClose={() => setNotificationsOpen(false)} />
                )}
             </div>

            {/* User Menu */}
            <div className="ml-3 relative" ref={userDropdownRef}>
              <div>
                <button
                  ref={userButtonRef}
                  onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                  aria-expanded={userDropdownOpen}
                  aria-haspopup="true"
                  className="flex items-center gap-3 focus:outline-none group"
                >
                  <div className="text-right hidden lg:block">
                     <p className="text-sm font-bold text-cream group-hover:text-white transition-colors">{userEmail?.split('@')[0]}</p>
                     <p className="text-xs text-cream/50 uppercase tracking-widest">Creator</p>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-warm-gray border border-warm-border flex items-center justify-center text-slate-blue font-bold shadow-md group-hover:border-slate-blue/50 transition-all">
                    {userEmail?.charAt(0).toUpperCase()}
                  </div>
                </button>
              </div>
              
              {userDropdownOpen && (
                <div className={`${dropdownContainerClass} right-0 left-auto w-48`}>
                  <NavLink to="/settings" className={dropdownLinkClass}>
                    <div className="flex items-center gap-2"><FiSettings className="w-4 h-4"/> Settings</div>
                  </NavLink>
                  <NavLink to="/help" className={dropdownLinkClass}>
                    <div className="flex items-center gap-2"><FiHelpCircle className="w-4 h-4"/> Help & Support</div>
                  </NavLink>
                  <div className="border-t border-cream/10 my-1"></div>
                  <button
                    onClick={() => { onLogout(); setUserDropdownOpen(false); }}
                    className="w-full text-left block px-6 py-3 text-sm text-soft-coral hover:bg-soft-coral/10 hover:text-red-400 transition-colors"
                  >
                    <div className="flex items-center gap-2 font-bold"><FiLogOut className="w-4 h-4"/> Sign Out</div>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile: Bell + Menu */}
          <div className="-mr-2 flex items-center gap-2 md:hidden">
            <div className="relative" ref={notificationsOpen ? notificationRef : undefined}>
              <button
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                className="relative p-2 rounded-xl text-cream/70 hover:text-white hover:bg-white/10"
              >
                <FiBell className="h-6 w-6" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-soft-coral text-white text-[9px] font-bold px-1">{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
              </button>
              {notificationsOpen && (
                <NotificationDropdown userId={userId} onClose={() => setNotificationsOpen(false)} />
              )}
            </div>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-label="Toggle navigation menu"
              className="p-2 rounded-xl text-cream/70 hover:text-white hover:bg-white/10 focus:outline-none"
            >
              <FiMenu className="h-8 w-8" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-warm-gray/95 backdrop-blur-xl border-t border-warm-border absolute w-full left-0 animate-fade-in-up shadow-2xl h-screen">
           <div className="px-6 py-8 space-y-4">
              <NavLink to="/dashboard" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 rounded-2xl text-xl font-bold text-cream hover:bg-cream/10">Dashboard</NavLink>
              <NavLink to="/projects" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 rounded-2xl text-xl font-bold text-cream hover:bg-cream/10">Projects</NavLink>
              <NavLink to="/content-hub" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 rounded-2xl text-xl font-bold text-cream hover:bg-cream/10">Content Hub</NavLink>
              <NavLink to="/trends" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 rounded-2xl text-xl font-bold text-cream hover:bg-cream/10">Trends</NavLink>
              <NavLink to="/calendar" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 rounded-2xl text-xl font-bold text-cream hover:bg-cream/10">Calendar</NavLink>
              <NavLink to="/analytics" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 rounded-2xl text-xl font-bold text-cream hover:bg-cream/10">Analytics</NavLink>
              <div className="border-t border-cream/10 my-4"></div>
              <NavLink to="/settings" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 rounded-2xl text-lg font-medium text-cream/70 hover:text-cream">Settings</NavLink>
              <button onClick={() => { onLogout(); setMobileMenuOpen(false); }} className="block w-full text-left px-4 py-3 rounded-2xl text-lg font-bold text-soft-coral hover:bg-soft-coral/10">Sign Out</button>
           </div>
        </div>
      )}
    </nav>
  );
}

export default TopNav;